import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Clock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await login(email, password);
            toast.success('Welcome back!');
            navigate('/dashboard');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Invalid credentials');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left side - Image */}
            <div className="hidden lg:flex lg:w-1/2 login-bg items-center justify-center p-12">
                <div className="text-white max-w-md animate-fade-in">
                    <div className="flex items-center gap-3 mb-8">
                        <div className="w-12 h-12 bg-orange-600 rounded-sm flex items-center justify-center">
                            <Clock className="w-7 h-7 text-white" />
                        </div>
                        <span className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            SwissTime
                        </span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight leading-none mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Precision Time Tracking
                    </h1>
                    <p className="text-lg text-zinc-300 leading-relaxed">
                        Track billable hours, manage projects, and generate accurate reports. Built for teams who value clarity and precision.
                    </p>
                </div>
            </div>

            {/* Right side - Form */}
            <div className="flex-1 flex items-center justify-center p-6 md:p-12 bg-zinc-50">
                <div className="w-full max-w-md animate-fade-in">
                    {/* Mobile logo */}
                    <div className="lg:hidden flex items-center gap-2 mb-8">
                        <div className="w-10 h-10 bg-orange-600 rounded-sm flex items-center justify-center">
                            <Clock className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-2xl font-extrabold tracking-tight text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            SwissTime
                        </span>
                    </div>

                    <div className="swiss-card">
                        <div className="mb-8">
                            <h2 className="text-3xl font-bold tracking-tight text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Welcome back
                            </h2>
                            <p className="text-zinc-500">
                                Sign in to your account to continue
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
                            <div className="form-group">
                                <Label htmlFor="email" className="form-label">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    required
                                    className="swiss-input"
                                    data-testid="login-email-input"
                                />
                            </div>

                            <div className="form-group">
                                <Label htmlFor="password" className="form-label">Password</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    required
                                    className="swiss-input"
                                    data-testid="login-password-input"
                                />
                            </div>

                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full swiss-btn-primary"
                                data-testid="login-submit-btn"
                            >
                                {loading ? 'Signing in...' : 'Sign In'}
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </form>

                        <div className="mt-6 text-center">
                            <p className="text-zinc-500">
                                Don't have an account?{' '}
                                <Link 
                                    to="/register" 
                                    className="text-orange-600 font-semibold hover:text-orange-700 transition-colors"
                                    data-testid="register-link"
                                >
                                    Create one
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
